import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';

const TABS = ['Overview', 'Financial', 'Sales', 'Purchases', 'HR'];
const today = new Date().toISOString().slice(0, 10);
const yearStart = `${today.slice(0, 4)}-01-01`;

const Reports = () => {
  const { error: showError } = useToast();
  const [tab, setTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [currencySymbol, setCurrencySymbol] = useState('£');

  const [fromDate, setFromDate] = useState(yearStart);
  const [toDate, setToDate] = useState(today);

  const [overview, setOverview] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashPosition, setCashPosition] = useState(null);
  const [arAging, setArAging] = useState(null);
  const [topCustomers, setTopCustomers] = useState(null);
  const [apAging, setApAging] = useState(null);
  const [hrOverview, setHrOverview] = useState(null);

  const loadCurrency = () => {
    Promise.all([apiClient.get('/organisations/current'), apiClient.get('/lookups/currencies')])
      .then(([orgRes, currRes]) => {
        const org = orgRes.data.data;
        const symbol = currRes.data.data.find((c) => c.id === org.baseCurrencyId)?.symbol || '£';
        setCurrencySymbol(symbol);
      })
      .catch(() => {});
  };

  const loadForTab = () => {
    setLoading(true);
    let request;
    if (tab === 'Overview') {
      request = apiClient.get('/dashboards/overview', { params: { fromDate, toDate } }).then(({ data }) => setOverview(data.data));
    } else if (tab === 'Financial') {
      request = Promise.all([
        apiClient.get('/dashboards/financial/profit-and-loss', { params: { fromDate, toDate } }),
        apiClient.get('/dashboards/financial/balance-sheet', { params: { asOfDate: toDate } }),
        apiClient.get('/dashboards/financial/cash-position'),
      ]).then(([pnlRes, bsRes, cashRes]) => {
        setPnl(pnlRes.data.data);
        setBalanceSheet(bsRes.data.data);
        setCashPosition(cashRes.data.data);
      });
    } else if (tab === 'Sales') {
      request = Promise.all([
        apiClient.get('/dashboards/sales/ar-aging', { params: { asOfDate: toDate } }),
        apiClient.get('/dashboards/sales/top-customers', { params: { limit: 10 } }),
      ]).then(([agingRes, topRes]) => {
        setArAging(agingRes.data.data);
        setTopCustomers(topRes.data.data);
      });
    } else if (tab === 'Purchases') {
      request = apiClient.get('/dashboards/purchases/ap-aging', { params: { asOfDate: toDate } }).then(({ data }) => setApAging(data.data));
    } else if (tab === 'HR') {
      request = apiClient.get('/dashboards/hr/overview').then(({ data }) => setHrOverview(data.data));
    }
    request.catch((err) => showError(getErrorMessage(err, 'Could not load report data'))).finally(() => setLoading(false));
  };

  useEffect(loadCurrency, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(loadForTab, [tab, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (n) => `${currencySymbol}${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Reports</h1>
        <p>Live financial and operational reporting, computed directly from your ledger</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap', alignItems: 'center' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center', paddingBottom: '0.5rem' }}>
          <label style={{ fontSize: '0.85rem', color: '#6b7280', textTransform: 'none' }}>Period:</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: '0.4rem' }} />
          <span style={{ color: '#6b7280' }}>to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: '0.4rem' }} />
        </div>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280', padding: '2rem', textAlign: 'center' }}>Loading...</p>
      ) : (
        <>
          {tab === 'Overview' && overview && (
            <>
              <section className="page-section">
                <div className="section-header"><h2>Executive Summary</h2></div>
                <div className="stats-row">
                  <div className="stat-box"><div className="label">Net Profit</div><div className="value" style={{ color: overview.profitAndLoss.netProfit >= 0 ? '#10b981' : '#ef4444' }}>{fmt(overview.profitAndLoss.netProfit)}</div></div>
                  <div className="stat-box"><div className="label">Cash on Hand</div><div className="value">{fmt(overview.cashPosition.totalCash)}</div></div>
                  <div className="stat-box"><div className="label">AR Outstanding</div><div className="value">{fmt(overview.arAging.totalOutstanding)}</div></div>
                  <div className="stat-box"><div className="label">AP Outstanding</div><div className="value">{fmt(overview.apAging.totalOutstanding)}</div></div>
                  <div className="stat-box"><div className="label">Active Headcount</div><div className="value">{overview.hrOverview.totalActiveHeadcount}</div></div>
                </div>
              </section>
              <section className="page-section">
                <div className="section-header"><h2>Balance Sheet Snapshot</h2></div>
                <div className="stats-row">
                  <div className="stat-box"><div className="label">Assets</div><div className="value">{fmt(overview.balanceSheet.assets)}</div></div>
                  <div className="stat-box"><div className="label">Liabilities</div><div className="value">{fmt(overview.balanceSheet.liabilities)}</div></div>
                  <div className="stat-box"><div className="label">Equity</div><div className="value">{fmt(overview.balanceSheet.equity)}</div></div>
                  <div className="stat-box">
                    <div className="label">Balanced</div>
                    <div className="value" style={{ color: overview.balanceSheet.balances ? '#10b981' : '#ef4444' }}>{overview.balanceSheet.balances ? 'Yes ✓' : 'No ✗'}</div>
                  </div>
                </div>
              </section>
              <section className="page-section">
                <div className="section-header"><h2>HR & Pending Items</h2></div>
                <div className="stats-row">
                  <div className="stat-box"><div className="label">Pending Leave Requests</div><div className="value">{overview.hrOverview.pendingLeaveRequests}</div></div>
                  <div className="stat-box"><div className="label">Pending Expense Claims</div><div className="value">{overview.hrOverview.pendingExpenseClaims}</div></div>
                </div>
              </section>
            </>
          )}

          {tab === 'Financial' && pnl && balanceSheet && cashPosition && (
            <>
              <section className="page-section">
                <div className="section-header"><h2>Profit & Loss ({pnl.fromDate} to {pnl.toDate})</h2></div>
                <div className="table-container">
                  <table className="data-table">
                    <tbody>
                      <tr><td>Revenue</td><td style={{ textAlign: 'right' }}>{fmt(pnl.revenue)}</td></tr>
                      <tr><td>Cost of Sales</td><td style={{ textAlign: 'right' }}>({fmt(pnl.costOfSales)})</td></tr>
                      <tr style={{ fontWeight: 700 }}><td>Gross Profit</td><td style={{ textAlign: 'right' }}>{fmt(pnl.grossProfit)}</td></tr>
                      <tr><td>Operating Expenses</td><td style={{ textAlign: 'right' }}>({fmt(pnl.operatingExpenses)})</td></tr>
                      <tr style={{ fontWeight: 700 }}><td>Operating Profit</td><td style={{ textAlign: 'right' }}>{fmt(pnl.operatingProfit)}</td></tr>
                      <tr><td>Other Income</td><td style={{ textAlign: 'right' }}>{fmt(pnl.otherIncome)}</td></tr>
                      <tr><td>Other Expense</td><td style={{ textAlign: 'right' }}>({fmt(pnl.otherExpense)})</td></tr>
                      <tr style={{ fontWeight: 700, borderTop: '2px solid #1f2937', color: pnl.netProfit >= 0 ? '#10b981' : '#ef4444' }}><td>Net Profit</td><td style={{ textAlign: 'right' }}>{fmt(pnl.netProfit)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="page-section">
                <div className="section-header"><h2>Balance Sheet (as of {balanceSheet.asOfDate})</h2></div>
                <div className="stats-row">
                  <div className="stat-box"><div className="label">Assets</div><div className="value">{fmt(balanceSheet.assets)}</div></div>
                  <div className="stat-box"><div className="label">Liabilities</div><div className="value">{fmt(balanceSheet.liabilities)}</div></div>
                  <div className="stat-box"><div className="label">Equity</div><div className="value">{fmt(balanceSheet.equity)}</div></div>
                  <div className="stat-box">
                    <div className="label">Balanced</div>
                    <div className="value" style={{ color: balanceSheet.balances ? '#10b981' : '#ef4444' }}>{balanceSheet.balances ? 'Yes ✓' : 'No ✗'}</div>
                  </div>
                </div>
              </section>

              <section className="page-section">
                <div className="section-header"><h2>Cash Position</h2></div>
                <div className="stats-row" style={{ marginBottom: '1rem' }}>
                  <div className="stat-box"><div className="label">Total Cash</div><div className="value">{fmt(cashPosition.totalCash)}</div></div>
                </div>
                {cashPosition.accounts.length > 0 && (
                  <div className="table-container">
                    <table className="data-table">
                      <thead><tr><th>Account</th><th>Balance</th></tr></thead>
                      <tbody>
                        {cashPosition.accounts.map((a) => (
                          <tr key={a.bankAccountId}><td>{a.accountName}</td><td>{fmt(a.balance)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}

          {tab === 'Sales' && arAging && topCustomers && (
            <>
              <section className="page-section">
                <div className="section-header"><h2>AR Aging (as of {arAging.asOfDate})</h2></div>
                <div className="stats-row" style={{ marginBottom: '1rem' }}>
                  <div className="stat-box"><div className="label">Total Outstanding</div><div className="value">{fmt(arAging.totalOutstanding)}</div></div>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Bucket</th><th>Outstanding</th><th>Invoices</th></tr></thead>
                    <tbody>
                      {arAging.buckets.map((b) => (
                        <tr key={b.label}><td style={{ textTransform: 'capitalize' }}>{b.label} days</td><td>{fmt(b.outstanding)}</td><td>{b.invoiceCount}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
              <section className="page-section">
                <div className="section-header"><h2>Top Customers</h2></div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Customer</th><th>Total Invoiced</th><th>Outstanding</th></tr></thead>
                    <tbody>
                      {topCustomers.length === 0 ? (
                        <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>No invoiced customers yet</td></tr>
                      ) : topCustomers.map((c) => (
                        <tr key={c.customerId}><td style={{ fontWeight: 600 }}>{c.customerName}</td><td>{fmt(c.totalInvoiced)}</td><td>{fmt(c.outstanding)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {tab === 'Purchases' && apAging && (
            <section className="page-section">
              <div className="section-header"><h2>AP Aging (as of {apAging.asOfDate})</h2></div>
              <div className="stats-row" style={{ marginBottom: '1rem' }}>
                <div className="stat-box"><div className="label">Total Outstanding</div><div className="value">{fmt(apAging.totalOutstanding)}</div></div>
              </div>
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>Bucket</th><th>Outstanding</th><th>Bills</th></tr></thead>
                  <tbody>
                    {apAging.buckets.map((b) => (
                      <tr key={b.label}><td style={{ textTransform: 'capitalize' }}>{b.label} days</td><td>{fmt(b.outstanding)}</td><td>{b.billCount}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {tab === 'HR' && hrOverview && (
            <>
              <section className="page-section">
                <div className="section-header"><h2>Workforce Overview</h2></div>
                <div className="stats-row">
                  <div className="stat-box"><div className="label">Active Headcount</div><div className="value">{hrOverview.totalActiveHeadcount}</div></div>
                  <div className="stat-box"><div className="label">Pending Leave Requests</div><div className="value">{hrOverview.pendingLeaveRequests}</div></div>
                  <div className="stat-box"><div className="label">Pending Expense Claims</div><div className="value">{hrOverview.pendingExpenseClaims}</div></div>
                </div>
              </section>
              <section className="page-section">
                <div className="section-header"><h2>Headcount by Department</h2></div>
                <div className="table-container">
                  <table className="data-table">
                    <thead><tr><th>Department</th><th>Headcount</th></tr></thead>
                    <tbody>
                      {hrOverview.byDepartment.length === 0 ? (
                        <tr><td colSpan={2} style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>No active employees yet</td></tr>
                      ) : hrOverview.byDepartment.map((d) => (
                        <tr key={d.departmentId || 'unassigned'}><td>{d.departmentName}</td><td>{d.headcount}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </motion.div>
  );
};

export default Reports;